import { useMemo, useState } from "react";
import { useCustom, useGetLocale, useTranslate } from "@refinedev/core";
import {
  Button,
  SelectField,
  StatusBadge,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-3): the Crematie-aanmeldingen worklist — the React port of the Jmix
// `AanmeldingenListView` (read-only). Dossiers still in the registration phase (CONCEPT / AANGEMELD /
// OPHALING_GEPLAND / OPGEHAALD), newest first. Columns: Dossier / Dier / Eigenaar / Herkomst /
// Status (badge) / Verwerkingsvorm / Aangemeld. Client-side search + status/herkomst/vorm filters.
// "Nieuwe aanmelding" still opens the Jmix wizard during coexistence (the create-wizard migration is a
// deferred write follow-up). The Jmix list view stays reachable by its @Route (strangler-fig).
//
// GET uses a RELATIVE url (the dataProvider prepends /api once). ROLE_CREMATORIUM_VIEW is
// server-enforced; a 403 surfaces inline.

interface AanmeldingRow {
  id: string;
  dossierNummer: string | null;
  dierNaam: string | null;
  eigenaarNaam: string | null;
  herkomst: string | null;
  status: string;
  verwerkingsvorm: string | null;
  aanmeldDatum: string | null;
}

const WERKLIJST_STATUSSEN = ["CONCEPT", "AANGEMELD", "OPHALING_GEPLAND", "OPGEHAALD"] as const;
const HERKOMSTEN = [
  "DOOR_EIGENAAR_GEBRACHT",
  "OPHALEN_BIJ_EIGENAAR",
  "VIA_DIERENARTS",
  "VIA_DERDE_PARTIJ",
] as const;
const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

const STATUS_TONE: Record<string, StatusTone> = {
  AANGEMELD: "success",
  OPHALING_GEPLAND: "warning",
};
const statusTone = (s: string): StatusTone => STATUS_TONE[s] ?? "neutral";

export function AanmeldingenList({ onNew }: { onNew?: () => void } = {}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError } = useCustom<AanmeldingRow[]>({
    url: "crematorium/aanmeldingen",
    method: "get",
  });
  const rows = data?.data ?? [];

  const [zoek, setZoek] = useState("");
  const [status, setStatus] = useState("");
  const [herkomst, setHerkomst] = useState("");
  const [vorm, setVorm] = useState("");

  const filtered = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (herkomst && r.herkomst !== herkomst) return false;
      if (vorm && r.verwerkingsvorm !== vorm) return false;
      if (!q) return true;
      return (
        (r.dossierNummer ?? "").toLowerCase().includes(q) ||
        (r.eigenaarNaam ?? "").toLowerCase().includes(q) ||
        (r.dierNaam ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, zoek, status, herkomst, vorm]);

  const columns: Column<AanmeldingRow>[] = [
    { header: t("aanmeldingen.col.dossier"), cell: (r) => r.dossierNummer ?? "—" },
    { header: t("aanmeldingen.col.dier"), cell: (r) => r.dierNaam ?? "—" },
    { header: t("aanmeldingen.col.eigenaar"), cell: (r) => r.eigenaarNaam ?? "—" },
    {
      header: t("aanmeldingen.col.herkomst"),
      cell: (r) => (r.herkomst ? t(`aanmeldingen.herkomst.${r.herkomst}`) : "—"),
    },
    {
      header: t("aanmeldingen.col.status"),
      cell: (r) => (
        <StatusBadge tone={statusTone(r.status)}>{t(`aanmeldingen.status.${r.status}`)}</StatusBadge>
      ),
    },
    {
      header: t("aanmeldingen.col.verwerkingsvorm"),
      cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : "—"),
    },
    {
      header: t("aanmeldingen.col.aangemeld"),
      cell: (r) => (r.aanmeldDatum ? formatDate(r.aanmeldDatum, locale) : "—"),
    },
  ];

  return (
    <div className="product-overview">
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <input
            type="search"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("aanmeldingen.search.placeholder")}
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            aria-label={t("aanmeldingen.search.placeholder")}
          />
          <SelectField
            label={t("aanmeldingen.col.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: "", label: t("aanmeldingen.filter.alle") },
              ...WERKLIJST_STATUSSEN.map((s) => ({ value: s, label: t(`aanmeldingen.status.${s}`) })),
            ]}
          />
          <SelectField
            label={t("aanmeldingen.col.herkomst")}
            value={herkomst}
            onChange={(e) => setHerkomst(e.target.value)}
            options={[
              { value: "", label: t("aanmeldingen.filter.alle") },
              ...HERKOMSTEN.map((h) => ({ value: h, label: t(`aanmeldingen.herkomst.${h}`) })),
            ]}
          />
          <SelectField
            label={t("aanmeldingen.col.verwerkingsvorm")}
            value={vorm}
            onChange={(e) => setVorm(e.target.value)}
            options={[
              { value: "", label: t("aanmeldingen.filter.alle") },
              ...VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) })),
            ]}
          />
          <span className="asiel-tarief__toolbar-spacer" />
          {onNew && <Button onClick={onNew}>{t("aanmeldingen.nieuw")}</Button>}
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("aanmeldingen.load.error")}
          </div>
        )}

        <IndeelbareTabel<AanmeldingRow> tabelKey="aanmeldingen-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("aanmeldingen.empty")}
        />
      </div>
    </div>
  );
}
